/**
 * DepthLogix site navigation — one model, rendered twice.
 *
 * The navbar used to hand-write every link in two places (the desktop
 * mega-menu and the mobile drawer) with four different slug helpers, so
 * adding a service meant four coordinated edits and a fifth group of
 * links had silently rotted into dead URLs.
 *
 * The labels below are the owner's own wording, kept verbatim. The paths
 * are NOT written here: each one is resolved out of the generated
 * `serviceCatalog`, so a nav link can only exist if the page behind it
 * exists. If a slug is ever renamed in the data files, the link drops out
 * and says so in development instead of shipping a blank page.
 */
import { SERVICE_CATEGORIES, SERVICES, findService } from "./serviceCatalog";

/**
 * Top-level items in their existing order.
 *
 * `Blog` was `/Blog` in the desktop nav and `/blog` in the mobile nav.
 * React Router matches case-insensitively, so both rendered — which meant
 * two crawlable URLs for one page and an active state that never matched.
 * The route is lowercase; both menus now use it.
 */
export const PRIMARY_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services", hasMegaMenu: true },
  { label: "Portfolio", to: "/portfolio" },
  { label: "About", to: "/about" },
  { label: "Blog", to: "/blog" },
];

/** The contact CTA. Wording is the owner's and is deliberate. */
export const NAV_CTA = { label: "Let's Talk", to: "/contact" };

/**
 * The four service groups, in menu order.
 *
 * `label` is the menu heading the site has always used — note that
 * Digital Marketing is titled "Digital Marketing & Ecommerce" in the nav
 * even though the catalog category is "Digital Marketing".
 *
 * Each entry is [catalog slug, menu label]. Several menu labels differ
 * from the page title on purpose ("UX/UI" opens "UI/UX Design"), so the
 * label is stated here and only the path comes from the catalog.
 *
 * A fifth group — "More Services": Quality Assurance, DevOps,
 * Cybersecurity, Cloud Applications — was removed. Those four built
 * two-segment `/services/<slug>` URLs that match no route and have no
 * entry in the catalog; rendered in both menus they were eight dead
 * links. They are not re-added here because there are no pages for them.
 */
const GROUP_SPEC = [
  {
    categoryKey: "digital-transformation",
    label: "Digital Transformation",
    items: [
      ["web-development", "Web Development"],
      ["app-development", "App Development"],
      ["custom-software", "Custom Software"],
      ["ui-ux-design", "UX/UI"],
      ["SaaS", "SaaS"],
    ],
  },
  {
    categoryKey: "emerging-tech",
    label: "Emerging Technologies",
    items: [
      ["enterprise-ai-solutions", "Enterprise AI Solutions"],
      ["ai-agents-ai-chatbots", "AI Agents/AI Chatbots"],
      ["generative-ai", "Generative AI"],
      ["ai-systems-integration", "AI Systems Integration"],
      ["data-analytics", "Data Analytics"],
    ],
  },
  {
    categoryKey: "business-apps",
    label: "Business Applications",
    items: [
      ["dynamics-365-erp", "Dynamics 365 ERP"],
      ["dynamics-365-crm", "Dynamics 365 CRM"],
      ["power-apps", "Power Apps"],
      ["salesforce", "Salesforce"],
      ["odoo", "Odoo"],
    ],
  },
  {
    categoryKey: "digital-marketing",
    label: "Digital Marketing & Ecommerce",
    items: [
      ["tiktok-shop-management", "TikTok Shop Management"],
      ["tiktok-ads", "TikTok ads"],
      ["meta-ads", "Meta ads"],
      ["ppc-ads", "PPC ads"],
      ["app-and-product-marketing", "App and Product Marketing"],
      ["email-marketing", "Email Marketing"],
      ["seo", "SEO"],
      ["design-&-development", "Design & Development"],
      ["maintenance-&-support", "Maintenance & Support"],
      ["automation-&-apps", "Automation & Apps"],
    ],
  },
];

/**
 * Resolved menu groups: `{ key, label, blurb, items: [{ label, to, title }] }`.
 * `blurb` is the category description from the catalog, not new copy.
 */
export const SERVICE_GROUPS = GROUP_SPEC.map((group) => {
  const category = SERVICE_CATEGORIES.find((c) => c.key === group.categoryKey);

  const items = group.items
    .map(([slug, label]) => {
      const service = findService(group.categoryKey, slug);

      if (!service) {
        // Loud in dev, silent in production — a missing catalog entry drops
        // the link rather than shipping a URL that renders nothing.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn(
            `[navigation] no catalog entry for ${group.categoryKey}/${slug}, link omitted`
          );
        }
        return null;
      }

      // `title` is the real H1 of the destination page; useful for tooltips
      // and for spotting label/page-title drift.
      return { label, to: service.path, title: service.title };
    })
    .filter(Boolean);

  return {
    key: group.categoryKey,
    label: group.label,
    blurb: category ? category.blurb : "",
    items,
  };
});

/** 25 — read from the catalog so the menu can never quote a stale number. */
export const SERVICE_COUNT = SERVICES.length;

/** True for `/services` and every service detail URL beneath it. */
export function isServicesPath(pathname) {
  return String(pathname).toLowerCase().startsWith("/services");
}
